/**
 * Proposta Comercial Formal — a variante que o cliente assina.
 *
 * Diferença de propósito em relação à variante `web`: aquela é peça de **convencimento**, com herói,
 * bloco institucional, gráfico e navegação. Esta é peça **contratual**. Tudo que não é condição,
 * valor ou obrigação sai do documento, porque em documento de adesão cada elemento decorativo é
 * ruído sobre o que o cliente está aceitando.
 *
 * A estrutura replica o modelo `Proposta Comercial Formal - Anestech.pdf`, que é o template atual do
 * Dynamics 365 e já passou pelo crivo comercial e jurídico da eMaster. Manter a mesma ordem e os
 * mesmos rótulos é deliberado: a substituição do gerador não deve obrigar a revalidar o documento
 * juridicamente.
 *
 * Ordem do modelo, preservada aqui:
 *
 *   1. Cabeçalho de identificação — ID da cotação, revisão, valor total, vigência, gerente de contas
 *   2. Oportunidade e cliente para cobrança
 *   3. Descrição do escopo
 *   4. Tabela de recursos, com total
 *   5. Créditos eMaster-AWS e total projetado
 *   6. Termo de adesão aos contratos registrados
 *   7. Condições comerciais
 *   8. Aceite do termo de adesão, com o bloco de assinatura
 */

import {
  descricaoExibivel,
  exigirFormalizacao,
  faixaSafeStorage,
  formatarBrl,
  formatarUsd,
  NOME_COMERCIAL_ASSESSMENT,
  PILAR_ASSESSMENT,
  totalMensalRecorrente,
  type DocumentoProposta,
  type FocoAssessment,
  type Formalizacao,
} from '@emaster/dominio';

import { escaparHtml, escaparJs } from './escapar.ts';
import { CONTAINER, FALLBACK_IMAGEM, ASSETS, icone } from './tokens.ts';
import { rotuloSessoes } from './treinamento.ts';

// ---------------------------------------------------------------------------
// Identificação da proponente
// ---------------------------------------------------------------------------

/**
 * Identificação da proponente.
 *
 * Obrigatória na variante formal: proposta sem CNPJ do proponente é inabilitada em licitação, e a
 * variante formal é justamente a que vai para pregão e dispensa.
 *
 * PENDENTE DE CONFIRMAÇÃO: os dados abaixo precisam ser confirmados pela empresa antes do uso em
 * licitação real. Ver docs/relatorio-testes-1.0.md.
 */
const PROPONENTE = {
  razaoSocial: 'eMaster Cloud & Security',
  cnpj: 'A CONFIRMAR',
  endereco: 'A CONFIRMAR',
} as const;

// ---------------------------------------------------------------------------
// Formatação
// ---------------------------------------------------------------------------

/** Data em `DD/MM/AAAA`, o formato do modelo. Sem `Date`, para não depender de fuso. */
function dataBr(iso: string): string {
  const [ano, mes, dia] = iso.split('-');
  return `${dia ?? ''}/${mes ?? ''}/${ano ?? ''}`;
}

/** CNPJ com a pontuação padrão. */
function formatarCnpj(digitos: string): string {
  return digitos.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

/** CEP com hífen. */
function formatarCep(digitos: string): string {
  return digitos.replace(/^(\d{5})(\d{3})$/, '$1-$2');
}

/**
 * Formata na moeda de faturamento da trilha.
 *
 * A moeda do documento formal é única, ao contrário da variante web que mostra USD e BRL lado a
 * lado: um documento assinável não pode deixar dúvida sobre em que moeda a obrigação é assumida.
 */
function moedaDaTrilha(
  doc: DocumentoProposta,
): (valor: { usd: number; brl: number | null }) => string {
  if (doc.premissas.trilha === 'PS') {
    return (v) => (v.brl === null ? formatarUsd(v.usd) : formatarBrl(v.brl));
  }
  return (v) => formatarUsd(v.usd);
}

/** Nome da moeda como aparece no rótulo do total, seguindo o modelo. */
function nomeMoeda(doc: DocumentoProposta): string {
  return doc.premissas.trilha === 'PS' ? 'Real' : 'US Dollar';
}

// ---------------------------------------------------------------------------
// 1. Cabeçalho de identificação
// ---------------------------------------------------------------------------

/**
 * Faixa de identificação do documento.
 *
 * O `ID DA COTAÇÃO` vem do Dynamics quando a sincronização já ocorreu. Enquanto não ocorreu, exibimos
 * o número da proposta com aviso explícito — em vez de deixar o campo vazio, que no modelo atual
 * costuma ser preenchido à mão e é fonte de divergência entre o documento e o CRM.
 */
function cabecalhoIdentificacao(doc: DocumentoProposta): string {
  const fmt = moedaDaTrilha(doc);

  const idCotacao = doc.sincronizacao.cotacaoNumero;
  const identificador =
    idCotacao === null
      ? `${escaparHtml(doc.numero)} <span class="text-brand-accent font-semibold">(cotação ainda não sincronizada)</span>`
      : escaparHtml(idCotacao);

  const campos: readonly { rotulo: string; valor: string }[] = [
    { rotulo: 'ID da Cotação', valor: identificador },
    { rotulo: 'Revisão', valor: String(doc.auditoria.versao - 1) },
    { rotulo: 'Valor Total', valor: fmt(doc.resultados.mensalidadeEstimada) },
    { rotulo: 'Início da Vigência', valor: dataBr(doc.emitidaEm) },
    { rotulo: 'Término da Vigência', valor: dataBr(doc.validaAte) },
    { rotulo: 'Gerente de Contas', valor: escaparHtml(doc.responsavel.nomeExibicao.toUpperCase()) },
  ];

  const linhas = campos
    .map(
      (c) => `<div class="flex justify-between gap-4 py-2 border-b border-gray-200 last:border-0">
                        <dt class="text-xs font-semibold uppercase tracking-wider text-gray-500">${escaparHtml(c.rotulo)}</dt>
                        <dd class="text-sm font-bold text-brand-blue text-right">${c.valor}</dd>
                    </div>`,
    )
    .join('\n                    ');

  return `
    <header class="bg-white border-b-4 border-brand-accent">
        <div class="${CONTAINER} py-10">
            <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-8 mb-8">
                <div>
                    <img src="${ASSETS.logoEmaster}" alt="eMaster Cloud &amp; Security" class="h-12 w-auto mb-6" ${FALLBACK_IMAGEM}>
                    <h1 class="text-4xl font-extrabold text-brand-blue tracking-tight">Proposta Comercial</h1>
                    <p class="text-gray-500 mt-2">eMaster Cloud &amp; Security &mdash; AWS Advanced Tier Services Partner</p>
                </div>
                <dl class="w-full sm:w-96 shrink-0 bg-gray-50 rounded-2xl border border-gray-200 p-6">
                    ${linhas}
                </dl>
            </div>
        </div>
    </header>`;
}

// ---------------------------------------------------------------------------
// 2. Oportunidade e cliente para cobrança
// ---------------------------------------------------------------------------

/** Bloco com a oportunidade do CRM e o endereço de cobrança do cliente. */
function blocoCliente(doc: DocumentoProposta, f: Formalizacao): string {
  const o = doc.oportunidade;

  const tituloOportunidade =
    o === null ? escaparHtml(doc.projeto) : escaparHtml(`${o.numero} - ${o.nome ?? doc.projeto}`);

  const c = f.clienteCobranca;
  const partes: string[] = [
    `<strong class="text-brand-blue">${escaparHtml(c.razaoSocial)}</strong>`,
  ];

  if (c.cnpj !== null) partes.push(`CNPJ ${escaparHtml(formatarCnpj(c.cnpj))}`);
  if (c.endereco !== null) partes.push(escaparHtml(c.endereco));

  const localidade = [c.cidade, c.uf].filter((v) => v !== null).join(' / ');
  if (localidade !== '') partes.push(escaparHtml(localidade));
  if (c.cep !== null) partes.push(`CEP ${escaparHtml(formatarCep(c.cep))}`);

  return `
    <section class="py-8 bg-white border-b border-gray-200">
        <div class="${CONTAINER}">
            <p class="text-lg font-bold text-brand-blue mb-6">${tituloOportunidade}</p>
            <div class="grid sm:grid-cols-2 gap-8">
                <div>
                    <h2 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Cliente para Cobrança</h2>
                    <address class="not-italic text-sm text-gray-600 leading-relaxed">
                        ${partes.join('<br>\n                        ')}
                    </address>
                </div>
                <div>
                    <h2 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Responsável Comercial</h2>
                    <p class="text-sm text-gray-600 leading-relaxed">
                        <strong class="text-brand-blue">${escaparHtml(doc.responsavel.nomeExibicao)}</strong><br>
                        ${escaparHtml(doc.responsavel.cargoExibicao)}<br>
                        <a href="mailto:${escaparHtml(doc.responsavel.email)}" class="text-brand-accent hover:underline">${escaparHtml(doc.responsavel.email)}</a><br>
                        ${escaparHtml(doc.responsavel.telefone)}
                    </p>
                </div>
            </div>
        </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// 3. Descrição do escopo
// ---------------------------------------------------------------------------

/**
 * Descrição do escopo, em prosa.
 *
 * No modelo do Dynamics este campo é um texto corrido único, resultado de concatenação, e sai
 * ilegível — o exemplo da Anestech tem itens numerados colados sem espaço. Aqui a mesma informação
 * vem estruturada do documento e é renderizada como lista, o que preserva o conteúdo e corrige a
 * apresentação.
 */
function blocoDescricao(doc: DocumentoProposta): string {
  const paragrafos = doc.conteudo.secoes.flatMap((s) => s.paragrafos);
  if (paragrafos.length === 0) return '';

  const itens = paragrafos
    .map((p) => `<li class="leading-relaxed">${escaparHtml(p)}</li>`)
    .join('\n                    ');

  return `
    <section class="py-8 bg-white border-b border-gray-200">
        <div class="${CONTAINER}">
            <h2 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-4">Descrição</h2>
            <p class="text-sm text-gray-700 leading-relaxed mb-4">${escaparHtml(doc.conteudo.promessaValor)}</p>
            <ul class="list-disc pl-5 space-y-2 text-sm text-gray-600">
                    ${itens}
            </ul>
        </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// 4. Tabela de recursos
// ---------------------------------------------------------------------------

interface LinhaRecurso {
  readonly descricao: string;
  readonly detalhe: string;
  readonly quantidade: number;
  readonly unitario: string;
  readonly total: string;
}

/**
 * Tabela de recursos e infraestrutura.
 *
 * Colunas iguais às do modelo: Descrição, Qtd, Valor Unit., Total. O `thead` repete em cada página
 * impressa por `display: table-header-group`, sem o que uma proposta com muitos recursos gera páginas
 * de tabela sem cabeçalho.
 */
function tabelaRecursos(
  linhas: readonly LinhaRecurso[],
  total: string,
  doc: DocumentoProposta,
): string {
  const corpo = linhas
    .map(
      (l) => `<tr class="border-b border-gray-200 align-top">
                            <td class="py-3 pr-4">
                                <p class="font-semibold text-brand-blue">${escaparHtml(l.descricao)}</p>
                                ${l.detalhe === '' ? '' : `<p class="text-xs text-gray-500 mt-1 leading-relaxed">${escaparHtml(l.detalhe)}</p>`}
                            </td>
                            <td class="py-3 px-4 text-center whitespace-nowrap">${String(l.quantidade)}</td>
                            <td class="py-3 px-4 text-right whitespace-nowrap">${l.unitario}</td>
                            <td class="py-3 pl-4 text-right font-bold text-brand-blue whitespace-nowrap">${l.total}</td>
                        </tr>`,
    )
    .join('\n                        ');

  return `
    <section class="py-8 bg-white border-b border-gray-200">
        <div class="${CONTAINER}">
            <h2 class="text-base font-bold text-brand-blue mb-6">Valor Mensal da Locação de Recursos e Infraestrutura em Nuvem</h2>
            <div class="overflow-x-auto">
                <table class="w-full text-sm border-collapse">
                    <thead class="tabela-recursos-cabecalho">
                        <tr class="border-b-2 border-brand-blue text-xs uppercase tracking-wider text-gray-600">
                            <th class="py-3 pr-4 text-left font-semibold">Descrição</th>
                            <th class="py-3 px-4 text-center font-semibold">Qtd</th>
                            <th class="py-3 px-4 text-right font-semibold">Valor Unit.</th>
                            <th class="py-3 pl-4 text-right font-semibold">Total</th>
                        </tr>
                    </thead>
                    <tbody class="text-gray-700">
                        ${corpo}
                    </tbody>
                    <tfoot>
                        <tr class="border-t-2 border-brand-blue">
                            <td class="py-4 pr-4 font-bold text-brand-blue uppercase text-xs tracking-wider" colspan="3">Total</td>
                            <td class="py-4 pl-4 text-right text-lg font-extrabold text-brand-blue whitespace-nowrap">${total}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
            <p class="text-xs text-gray-500 mt-4 leading-relaxed">
                Valores em ${escaparHtml(nomeMoeda(doc))}. O consumo de infraestrutura é medido na própria conta AWS e faturado conforme o uso efetivo do mês.
            </p>
        </div>
    </section>`;
}

/** Monta as linhas da tabela a partir dos resultados calculados. */
function linhasRecursos(doc: DocumentoProposta): LinhaRecurso[] {
  const fmt = moedaDaTrilha(doc);
  const r = doc.resultados;
  const linhas: LinhaRecurso[] = [];

  const itens = doc.premissas.itensCusto;
  const ptax = doc.premissas.ptax;

  if (r.margemAplicada && itens !== null && itens.length > 0) {
    const fator = 1 - r.margemPercentual / 100;
    for (const item of itens) {
      const usd = Math.round((item.custoMensalLiquidoUsd / fator) * 100) / 100;
      const valor = { usd, brl: ptax === null ? null : Math.round(usd * ptax * 100) / 100 };
      // No documento assinável a coluna Descrição é uma célula única: nome do serviço em destaque e
      // o detalhamento abaixo. A descrição do CSV entra à frente da memória de cálculo, porque é o
      // que identifica a função do recurso no ambiente do cliente.
      const contexto = descricaoExibivel(item.descricao);
      const detalhe = [contexto, item.resumoConfiguracao].filter((t) => t !== '').join(' — ');

      linhas.push({
        descricao: item.servico,
        detalhe,
        quantidade: 1,
        unitario: fmt(valor),
        total: fmt(valor),
      });
    }
  } else if (r.consumo.usd > 0) {
    linhas.push({
      descricao: 'Infraestrutura AWS',
      detalhe: r.margemAplicada
        ? `Dimensionada com margem de gestão de ${String(r.margemPercentual)}%`
        : 'Conforme dimensionamento acordado',
      quantidade: 1,
      unitario: fmt(r.consumo),
      total: fmt(r.consumo),
    });
  }

  if (r.custoSuporte.usd > 0) {
    linhas.push({
      descricao: `SafePlace ${doc.premissas.planoSuporte}`,
      detalhe: 'Sustentação, monitoramento e acompanhamento consultivo do ambiente AWS.',
      quantidade: 1,
      unitario: fmt(r.custoSuporte),
      total: fmt(r.custoSuporte),
    });
  }

  if (r.licencasBackup.usd > 0) {
    // Num documento assinável a base de preço precisa estar declarada: o preço de tabela é em reais
    // e o valor em dólar é conversão pelo câmbio informado no cabeçalho. Ver `investimento.ts`.
    linhas.push({
      descricao: 'eMaster Backup On-Premises',
      detalhe:
        doc.premissas.trilha === 'PS'
          ? 'Licenças mensais conforme quantidade contratada.'
          : 'Licenças mensais com preço fixo em reais, convertido pelo câmbio declarado.',
      quantidade: 1,
      unitario: fmt(r.licencasBackup),
      total: fmt(r.licencasBackup),
    });
  }

  if (r.plataformaSafeStorage.usd > 0 && doc.premissas.safestorage !== null) {
    // O termo é assinável, então precisa deixar registrado que a assinatura não inclui o
    // armazenamento — é a cláusula que evita a discussão sobre o que foi contratado.
    const s = doc.premissas.safestorage;

    linhas.push({
      descricao: faixaSafeStorage(s.plano).nomeComercial,
      detalhe:
        `Assinatura mensal da plataforma de arquivos para ${String(s.usuarios)} ` +
        `${s.usuarios === 1 ? 'usuário' : 'usuários'}. O armazenamento no Amazon S3 é faturado ` +
        'sob demanda, conforme o volume utilizado, e consta nas linhas de infraestrutura.',
      quantidade: 1,
      unitario: fmt(r.plataformaSafeStorage),
      total: fmt(r.plataformaSafeStorage),
    });
  }

  if (r.custosAdicionais.usd > 0) {
    /*
     * No termo assinável cada item vai em linha própria, ao contrário da tabela de investimento.
     *
     * Aqui a diferença se justifica: o termo é o anexo que descreve o que está sendo contratado, e
     * "Custos adicionais R$ X" não é objeto contratual — é rubrica. O valor unitário fica em branco
     * porque a conversão por item não é feita pelo motor, e inventá-la aqui faria a soma das linhas
     * divergir do total. O que o termo precisa garantir é que cada serviço contratado esteja nomeado.
     */
    const itens = doc.premissas.custosAdicionais ?? [];

    if (itens.length === 1 && itens[0] !== undefined) {
      linhas.push({
        descricao: itens[0].descricao,
        detalhe:
          'Serviço contratado à parte, com valor mensal acordado e sem variação por consumo.',
        quantidade: 1,
        unitario: fmt(r.custosAdicionais),
        total: fmt(r.custosAdicionais),
      });
    } else {
      linhas.push({
        descricao: 'Custos adicionais',
        detalhe: `Serviços contratados à parte, com valor mensal acordado: ${itens
          .map((i) => i.descricao)
          .join('; ')}.`,
        quantidade: itens.length,
        unitario: '',
        total: fmt(r.custosAdicionais),
      });
    }
  }

  return linhas;
}

// ---------------------------------------------------------------------------
// 5. Créditos e total projetado
// ---------------------------------------------------------------------------

/**
 * Créditos eMaster-AWS e total projetado.
 *
 * O modelo traz a linha "Créditos eMaster-aws" sempre, mesmo vazia. Aqui ela só aparece quando há
 * crédito pleiteado, e **sempre acompanhada do disclaimer de não-garantia** — obrigatório porque o
 * crédito depende de decisão da AWS, não da eMaster. Omitir isso num documento assinável criaria
 * expectativa contratual sobre algo que a eMaster não controla.
 */
function blocoCreditos(doc: DocumentoProposta): string {
  const fmt = moedaDaTrilha(doc);
  const credito = doc.premissas.credito;
  const r = doc.resultados;

  const temCredito = credito !== null && credito.valorUsd > 0;

  const linhaCredito = temCredito
    ? `<div class="flex justify-between items-baseline gap-4 py-3 border-b border-gray-200">
                    <span class="text-sm text-gray-600">Créditos eMaster-AWS pleiteados &mdash; redução mensal nos primeiros ${String(credito.janelaMeses)} meses</span>
                    <span class="text-sm font-bold text-green-700 whitespace-nowrap">&minus; ${fmt(r.reducaoMensal)}</span>
                </div>`
    : '';

  const avisoCredito = temCredito
    ? `<div class="mt-6 bg-orange-50 border-l-4 border-brand-accent p-5 rounded-r-lg">
                <p class="text-sm text-gray-700 leading-relaxed">
                    <strong class="text-brand-blue">O pleito de créditos não constitui garantia de concessão.</strong>
                    A concessão é decisão exclusiva da AWS. O valor de ${formatarUsd(credito.valorUsd)} representa o teto que a eMaster se compromete a solicitar, não um valor assegurado. Aprovação, montante final, vigência e condições de uso dependem da análise conjunta eMaster + AWS.
                </p>
            </div>`
    : '';

  return `
    <section class="py-8 bg-gray-50 border-b border-gray-200">
        <div class="${CONTAINER}">
            <div class="max-w-2xl ml-auto">
                ${linhaCredito}
                <div class="flex justify-between items-baseline gap-4 pt-4">
                    <span class="text-sm font-bold text-brand-blue uppercase tracking-wider">Total mensal projetado<sup>*</sup> em ${escaparHtml(nomeMoeda(doc))}</span>
                    <span class="text-2xl font-extrabold text-brand-blue whitespace-nowrap">${fmt(r.mensalidadeEstimada)}</span>
                </div>
            </div>
            <p class="text-xs text-gray-500 mt-4 leading-relaxed">
                <sup>*</sup> O valor a ser faturado poderá flutuar devido a recursos adicionais incluídos, ao número total de horas de cada mês ou à redução de consumo.
            </p>
            ${avisoCredito}
        </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// 5b. Cortesias incluídas
// ---------------------------------------------------------------------------

/** Cartão de uma cortesia, com título e os parágrafos que delimitam o compromisso. */
function cartaoCortesia(titulo: string, paragrafos: readonly string[]): string {
  const corpo = paragrafos
    .map((p) => `<p class="text-sm text-gray-600 leading-relaxed mt-2">${p}</p>`)
    .join('\n                        ');

  return `<div class="border border-gray-200 rounded-lg p-6">
                <div class="flex items-start gap-3">
                    <span class="shrink-0 mt-0.5 text-brand-accent">${icone('gift', 'w-5 h-5')}</span>
                    <div>
                        <p class="text-sm font-bold text-brand-blue">${escaparHtml(titulo)}</p>
                        ${corpo}
                    </div>
                </div>
            </div>`;
}

/**
 * Serviços entregues sem custo, declarados no documento assinável.
 *
 * Cortesia prometida na peça de convencimento e ausente do termo é a pior combinação possível: o
 * cliente cobra o que leu na proposta e o contrato não sustenta. Por isso cada cortesia aparece aqui
 * com escopo e entregável nomeados, e não como menção genérica.
 *
 * O bloco é um só para as duas cortesias, e não um por tipo: a lista cresce por item, e seções
 * separadas fariam o termo repetir o mesmo cabeçalho de cortesia duas vezes.
 *
 * @returns HTML do bloco, ou string vazia quando não há cortesia alguma a declarar.
 */
function blocoCortesias(doc: DocumentoProposta): string {
  /** Escopo declarado de cada foco. Texto contratual: precisa delimitar o que será examinado. */
  const ESCOPO: Readonly<Record<FocoAssessment, string>> = {
    custos:
      'dimensionamento de recursos, cobertura de planos de economia, classes de armazenamento, transferência de dados e governança de custos',
    seguranca:
      'gestão de identidade e acesso, criptografia, exposição de rede, trilha de auditoria, backup e recuperação, e aderência à LGPD',
  };

  const itens: string[] = [];

  for (const foco of doc.premissas.assessment ?? []) {
    itens.push(
      cartaoCortesia(NOME_COMERCIAL_ASSESSMENT[foco], [
        `Avaliação do ambiente conduzida por arquitetos certificados da eMaster, orientada pelo pilar ${escaparHtml(PILAR_ASSESSMENT[foco])} do AWS Well-Architected Framework, abrangendo ${escaparHtml(ESCOPO[foco])}.`,
        'Entregáveis: relatório de achados priorizados, plano de ação e sessão de apresentação dos resultados. A execução é agendada com o time do cliente após a aprovação desta proposta, e o relatório permanece com o cliente independentemente de contratação adicional.',
      ]),
    );
  }

  const sessoes = doc.premissas.sessoesTreinamento;
  if (sessoes > 0) {
    /*
     * A quantidade é o compromisso que precisa estar escrito. O conteúdo, não: a pauta é acordada
     * com o cliente, e fixá-la aqui transformaria em cláusula um programa que ainda será definido.
     */
    itens.push(
      cartaoCortesia(`Treinamento AWS Showroom — ${escaparHtml(rotuloSessoes(sessoes))}`, [
        `${escaparHtml(rotuloSessoes(sessoes))} de capacitação do time do cliente, conduzidas por arquitetos certificados AWS da eMaster, em formato remoto ou presencial.`,
        'A pauta de cada sessão é acordada com o time do cliente antes da realização e parte dos serviços contratados nesta proposta. O agendamento ocorre após a aprovação.',
      ]),
    );
  }

  if (itens.length === 0) return '';

  return `
    <section class="py-8 bg-white border-b border-gray-200">
        <div class="${CONTAINER}">
            <h2 class="text-sm font-bold text-brand-blue uppercase tracking-wider mb-4">Serviços inclusos sem custo</h2>
            <div class="space-y-4">
            ${itens.join('\n            ')}
            </div>
        </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// 6. Termo de adesão aos contratos
// ---------------------------------------------------------------------------

/** Contratos registrados aos quais o cliente adere. É o que dá natureza contratual ao documento. */
function blocoTermoAdesao(f: Formalizacao): string {
  const itens = f.contratos
    .map(
      (c) => `<li class="flex items-start gap-3">
                        <span class="shrink-0 mt-0.5 text-brand-accent">${icone('file-text', 'w-4 h-4')}</span>
                        <span>${escaparHtml(c.titulo)} &mdash; <strong class="text-brand-blue">Registro nº ${escaparHtml(c.registro)}</strong></span>
                    </li>`,
    )
    .join('\n                    ');

  return `
    <section class="py-8 bg-white border-b border-gray-200">
        <div class="${CONTAINER}">
            <h2 class="text-base font-bold text-brand-blue mb-4">Termo de Adesão aos Contratos</h2>
            <ul class="space-y-3 text-sm text-gray-700 mb-4">
                    ${itens}
            </ul>
            <p class="text-xs text-gray-500 leading-relaxed">${escaparHtml(f.cartorio)}</p>
        </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// 7. Condições comerciais
// ---------------------------------------------------------------------------

/**
 * Condições comerciais.
 *
 * As frases são montadas pelo domínio, em `condicoesComerciaisTexto`, e não escritas aqui: a mesma
 * lista precisa ir para o campo `CommercialTerms` da Cotação no Dynamics. Se cada superfície
 * montasse o seu texto, o CRM e o documento assinado divergiriam — exatamente o risco que a
 * integração existe para eliminar.
 */
function blocoCondicoes(condicoes: readonly string[]): string {
  const itens = condicoes
    .map(
      (c) => `<li class="flex items-start gap-3">
                        <span class="shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-brand-accent"></span>
                        <span class="leading-relaxed">${escaparHtml(c)}</span>
                    </li>`,
    )
    .join('\n                    ');

  return `
    <section class="py-8 bg-white border-b border-gray-200">
        <div class="${CONTAINER}">
            <h2 class="text-base font-bold text-brand-blue mb-4">Condições Comerciais</h2>
            <ul class="space-y-2.5 text-sm text-gray-700">
                    ${itens}
            </ul>
        </div>
    </section>`;
}

// ---------------------------------------------------------------------------
// 8. Aceite
// ---------------------------------------------------------------------------

/**
 * Bloco de aceite.
 *
 * Dois estados mutuamente exclusivos, e a distinção é importante:
 *
 * - **não assinado**: campos em branco para assinatura, como no modelo, mais o botão de aceite
 *   eletrônico quando o meio configurado permite. O botão é `no-print`: no papel, valem as linhas.
 * - **assinado**: o comprovante, com signatário, instante e hash do documento. Substitui as linhas
 *   em branco, porque um documento já aceito não deve exibir campos que sugiram reassinatura.
 */
function blocoAceite(doc: DocumentoProposta, f: Formalizacao): string {
  const a = f.aceite;

  if (a.assinadoEm !== null) {
    const linhas: readonly { rotulo: string; valor: string }[] = [
      { rotulo: 'Aceito por', valor: a.assinadoPor ?? '—' },
      { rotulo: 'Documento', valor: a.documentoIdentificacao ?? '—' },
      { rotulo: 'Data e hora', valor: a.assinadoEm },
      { rotulo: 'Meio', valor: a.meio === 'clique_eletronico' ? 'Aceite eletrônico' : a.meio },
      { rotulo: 'Impressão digital (SHA-256)', valor: a.hashDocumento ?? '—' },
    ];

    const corpo = linhas
      .map(
        (
          l,
        ) => `<div class="flex flex-col sm:flex-row sm:justify-between gap-1 py-2 border-b border-green-200 last:border-0">
                        <dt class="text-xs font-semibold uppercase tracking-wider text-green-800">${escaparHtml(l.rotulo)}</dt>
                        <dd class="text-sm text-gray-700 break-all sm:text-right">${escaparHtml(l.valor)}</dd>
                    </div>`,
      )
      .join('\n                    ');

    return `
    <section class="py-10 bg-white">
        <div class="${CONTAINER}">
            <div class="bg-green-50 border-2 border-green-600 rounded-2xl p-8">
                <div class="flex items-center gap-3 mb-6">
                    <span class="text-green-700">${icone('check-circle-2', 'w-7 h-7')}</span>
                    <h2 class="text-lg font-bold text-green-900">Proposta aceita</h2>
                </div>
                <dl>
                    ${corpo}
                </dl>
            </div>
        </div>
    </section>`;
  }

  // O botão só aparece quando o aceite eletrônico é o meio previsto. Em `manual`, o documento é
  // assinado no papel ou por provedor externo, e um botão ali seria promessa que o sistema não cumpre.
  const botao =
    a.meio === 'clique_eletronico'
      ? `<div class="no-print mt-8 pt-8 border-t border-gray-300">
                    <p class="text-sm text-gray-600 leading-relaxed mb-5">
                        Você pode aceitar esta proposta eletronicamente. O aceite registra data, hora e a impressão digital deste documento, e tem a mesma validade da assinatura acima.
                    </p>
                    <button type="button" id="botao-aceitar"
                        class="inline-flex items-center gap-3 px-8 py-4 bg-brand-accent text-white font-bold rounded-xl hover:bg-orange-600 transition-colors shadow-lg shadow-orange-500/20">
                        ${icone('file-signature', 'w-5 h-5')}
                        Aceitar e assinar esta proposta
                    </button>
                    <p class="text-xs text-gray-500 mt-4">Ao aceitar, você declara ter poderes para representar a contratante.</p>
                </div>`
      : '';

  const campos: readonly string[] = ['Responsável', 'RG ou CPF', 'Assinatura', 'Data'];
  const linhasAssinatura = campos
    .map(
      (c) => `<div class="pt-8">
                        <div class="border-b border-gray-500"></div>
                        <p class="text-xs uppercase tracking-wider text-gray-600 mt-2">${escaparHtml(c)}</p>
                    </div>`,
    )
    .join('\n                    ');

  return `
    <section class="py-10 bg-white">
        <div class="${CONTAINER}">
            <div class="bg-gray-50 border border-gray-300 rounded-2xl p-8">
                <h2 class="text-base font-bold text-brand-blue mb-2">Aceite do Termo de Adesão</h2>
                <p class="text-sm text-gray-600 leading-relaxed mb-4">
                    A assinatura abaixo formaliza a adesão de ${escaparHtml(f.clienteCobranca.razaoSocial)} aos contratos relacionados e às condições comerciais desta proposta, válida até ${dataBr(doc.validaAte)}.
                </p>
                <div class="grid sm:grid-cols-2 gap-x-10">
                    ${linhasAssinatura}
                </div>
                ${botao}
            </div>
        </div>
    </section>`;
}

/**
 * Script do aceite eletrônico.
 *
 * Deliberadamente **não** conclui o aceite no cliente. Ele coleta os dados e chama o endpoint, que é
 * quem calcula o hash do documento, registra o instante pelo relógio do servidor e grava a evidência.
 *
 * A razão é a mesma da decisão D6 aplicada a preço: o navegador não é autoridade. Um aceite cuja
 * evidência viesse do relógio e do hash calculados no cliente não sustentaria contestação.
 *
 * `URL_ACEITE` é substituída na publicação. Sem endpoint configurado, o botão explica em vez de
 * falhar silenciosamente.
 */
export function scriptAceite(urlAceite: string | null): string {
  const acao =
    urlAceite === null
      ? `
            alert('O aceite eletrônico ainda não está habilitado para esta proposta. Assine o documento impresso ou fale com o seu contato comercial.');`
      : `
            var nome = prompt('Nome completo do responsável:');
            if (!nome) return;
            var documento = prompt('RG ou CPF do responsável:');
            if (!documento) return;

            botao.disabled = true;
            botao.textContent = 'Registrando aceite...';

            fetch('${escaparJs(urlAceite)}', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ assinadoPor: nome, documentoIdentificacao: documento })
            })
            .then(function (resposta) {
                if (!resposta.ok) throw new Error('HTTP ' + resposta.status);
                return resposta.json();
            })
            .then(function () {
                alert('Aceite registrado. Uma via assinada será enviada por e-mail.');
                location.reload();
            })
            .catch(function () {
                alert('Não foi possível registrar o aceite agora. Tente novamente ou fale com o seu contato comercial.');
                botao.disabled = false;
                botao.textContent = 'Aceitar e assinar esta proposta';
            });`;

  return `
        (function () {
            var botao = document.getElementById('botao-aceitar');
            if (!botao) return;
            botao.addEventListener('click', function () {${acao}
            });
        })();`;
}

// ---------------------------------------------------------------------------
// Montagem
// ---------------------------------------------------------------------------

/** Rodapé do documento formal. Sem CTA e sem link externo: é peça contratual. */
function rodapeFormal(doc: DocumentoProposta): string {
  return `
    <footer class="bg-brand-blue text-white py-10">
        <div class="${CONTAINER}">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <div>
                    <p class="font-bold">${escaparHtml(PROPONENTE.razaoSocial)}</p>
                    <p class="text-sm text-gray-300 mt-1">CNPJ: ${escaparHtml(PROPONENTE.cnpj)}</p>
                    <p class="text-sm text-gray-300 mt-1">${escaparHtml(PROPONENTE.endereco)}</p>
                    <p class="text-sm text-gray-300 mt-1">AWS Advanced Tier Services Partner</p>
                </div>
                <div class="text-sm text-gray-300 sm:text-right">
                    <p>Proposta ${escaparHtml(doc.numero)} &mdash; revisão ${String(doc.auditoria.versao - 1)}</p>
                    <p class="mt-1">Válida até ${dataBr(doc.validaAte)}.</p>
                </div>
            </div>
        </div>
    </footer>`;
}

/**
 * Corpo da proposta comercial formal.
 *
 * @param doc Documento validado, com `formalizacao` preenchida.
 * @param condicoes Condições comerciais já montadas pelo domínio.
 * @throws {ErroPublicacao} quando falta o bloco de formalização.
 */
export function corpoFormal(doc: DocumentoProposta, condicoes: readonly string[]): string {
  const f = exigirFormalizacao(doc);

  return [
    cabecalhoIdentificacao(doc),
    blocoCliente(doc, f),
    blocoDescricao(doc),
    // A tabela fecha sem abater o crédito: o abatimento é a primeira linha de `blocoCreditos`,
    // logo abaixo, que então apresenta o total projetado. Num documento assinável, subtrair um
    // incentivo condicionado dentro do total dos recursos esconderia o preço efetivamente
    // contratado. Ver o comentário equivalente em `investimento.ts`.
    tabelaRecursos(
      linhasRecursos(doc),
      moedaDaTrilha(doc)(totalMensalRecorrente(doc.resultados)),
      doc,
    ),
    blocoCreditos(doc),
    blocoCortesias(doc),
    blocoTermoAdesao(f),
    blocoCondicoes(condicoes),
    blocoAceite(doc, f),
    rodapeFormal(doc),
  ]
    .filter((p) => p !== '')
    .join('\n');
}
